import React from 'react';
import {keepPreviousData, useQuery} from '@tanstack/react-query';
import usePreloadDeferredPayments from './usePreloadDeferredPayments';
import {QUERY_CACHE_KEYS,CartPayment} from '@chekin/core';
import {useChekinSDK} from '../../context/ChekinSDK';
import {useReservation} from '../../context/reservation';
import {usePaymentsStore} from '../../store/payments';
import {API} from '@chekin/api';

const CACHE_TIME = 5_000;
const STALE_TIME = 5_000;

function useDeferredPayments(defaultDeferredPayments?: CartPayment[]) {
  const deferredPayments = usePaymentsStore(state => state.deferredPayments);
  const addDeferredPayments = usePaymentsStore(state => state.addDeferredPayments);
  const removeDeferredPayment = usePaymentsStore(state => state.removeDeferredPayment);

  const deferredPaymentsIds = React.useMemo(() => {
    return deferredPayments.map(p => p.id);
  }, [deferredPayments]);

  React.useEffect(
    function setDefaultPayments() {
      if (!deferredPayments?.length && defaultDeferredPayments?.length) {
        addDeferredPayments(defaultDeferredPayments);
      }
    },
    [addDeferredPayments, defaultDeferredPayments, deferredPayments?.length],
  );

  return {
    deferredPayments,
    deferredPaymentsIds,
    addDeferredPayments,
    removeDeferredPayment,
  };
}

type UsePaymentsCartProps = {
  refetchOnMount?: boolean;
  refetchOnWindowFocus?: boolean;
  defaultDeferredPayments?: CartPayment[];
  enabled?: boolean;
};
function usePaymentsCart({
  refetchOnMount = true,
  refetchOnWindowFocus = false,
  defaultDeferredPayments,
  enabled = true,
}: UsePaymentsCartProps = {}) {
  const {isTokenValid} = useChekinSDK();
  const {data} = useReservation();
  const {deferredPaymentsIds, addDeferredPayments, removeDeferredPayment} =
    useDeferredPayments(defaultDeferredPayments);
  const {isPreloading} = usePreloadDeferredPayments({
    addDeferredPayments,
    removeDeferredPayment,
  });

  const {
    data: paymentsCart,
    isLoading: isPaymentsCartLoading,
    isFetching: isPaymentsCartFetching,
  } = useQuery({
    queryKey: [QUERY_CACHE_KEYS.paymentsCart, deferredPaymentsIds, data?.id],
    queryFn: () =>
      API.payments.getPaymentsCart(
        `reservation=${data?.id}&pay_later=${deferredPaymentsIds?.toString()}`,
      ),
    placeholderData: keepPreviousData,
    enabled: Boolean(!isPreloading && data?.id && isTokenValid && enabled),
    refetchOnWindowFocus,
    refetchOnMount,
    gcTime: CACHE_TIME,
    staleTime: STALE_TIME,
  });

  const selectedPayments = paymentsCart?.pay_now_payments || [];
  const isPaymentsCartEmpty =
    !paymentsCart?.pay_now_payments?.length &&
    !paymentsCart?.pay_later_payments?.length &&
    !isPaymentsCartLoading &&
    !isPaymentsCartFetching;

  return {
    paymentsCart,
    isPaymentsCartLoading,
    isPaymentsCartFetching,
    selectedPayments,
    isPaymentsCartEmpty,
    addDeferredPayments,
    removeDeferredPayment,
  };
}

export {usePaymentsCart};
